/**
 * /feedback — опитування «як покращити inFLOW» (owner 2026-08-11).
 *
 * ⚠️ Каркас сторінки — СТАНДАРТНИЙ, як у Подкастах і Базі знань:
 * `BackNav` + `PageHeader` (з subtitle) + `Section`. Перша версія малювала
 * власний hero і власні `px-5` поверх падінгів `Layout`, а пояснення сиділо в
 * окремій картці з плиткою — через це відступи не збігались із рештою апки
 * (owner: «не слідуєш правилам дизайну нашої апки»). Не повторювати: нова
 * сторінка = ті самі три компоненти.
 */
import { useEffect, useState } from "react";
import { BackNav } from "@/components/BackNav";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/Section";
import { Spinner } from "@/components/Spinner";
import { useFeedbackState, useGiftWindow, useSubmitFeedback } from "@/api/hooks";
import { FeedbackResultModal } from "@/components/FeedbackResultModal";
import { VoiceBioButton } from "@/components/VoiceBioButton";
import { GiftOfferModal } from "@/components/GiftOfferModal";
import { useNavigate } from "react-router-dom";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";

export function FeedbackPage() {
  const navigate = useNavigate();
  const stateQuery = useFeedbackState();
  const giftQuery = useGiftWindow();
  const submitMut = useSubmitFeedback();
  // ⚠️ Чернетку читаємо ОДРАЗУ в ініціалізаторі стану, а не в useEffect.
  // Було: ефект-відновлення і ефект-збереження на маунті виконувались підряд,
  // і другий встигав записати порожній рядок ПОВЕРХ збереженого тексту —
  // після будь-якого перезавантаження (а воно буває при оновленні апки)
  // людина втрачала написане (owner 2026-08-11: «написав 500 символів — усе
  // стерло»).
  const [text, setText] = useState(
    () => localStorage.getItem("feedback_draft") ?? "",
  );
  const [result, setResult] = useState<
    { useful: boolean; reason: string; rewarded: boolean } | null
  >(null);
  // Подарунок, що вже чекає, показуємо попапом при кожному заході (owner
  // 2026-08-11): людина приходить по тому самому посиланню й має бачити
  // подарунок, а не порожню форму.
  const [giftShown, setGiftShown] = useState(false);

  // Зберігаємо лише НЕПОРОЖНЄ: інакше перший рендер стирає чернетку.
  useEffect(() => {
    if (text.trim()) localStorage.setItem("feedback_draft", text);
  }, [text]);

  if (stateQuery.isLoading) return <Spinner />;

  const min = stateQuery.data?.min_chars ?? 500;
  const attemptsLeft = stateQuery.data?.attempts_left ?? 3;
  // Рахуємо ТАК САМО, як сервер: ланцюжок пробілів/переносів = один символ.
  // Інакше лічильник показував 502, а сервер відмовляв (owner 2026-08-12).
  const len = text.replace(/\s+/g, " ").trim().length;
  const enough = len >= min;
  const canSend = enough && attemptsLeft > 0 && !submitMut.isPending;
  // Стан нагороди (owner 2026-08-11): якщо подарунок уже чекає — показуємо
  // його попапом, а не форму «ніби нічого не було»; якщо вже використаний —
  // прямо про це кажемо.
  const rewardState = stateQuery.data?.reward_state ?? "none";
  const feedbackGift = (giftQuery.data?.offers ?? []).find(
    (o) => o.offer === "feedback14",
  );

  const handleSubmit = async () => {
    if (!canSend) return;
    try {
      const res = await submitMut.mutateAsync(text.trim());
      tg()?.HapticFeedback?.notificationOccurred?.(
        res.useful ? "success" : "warning",
      );
      setResult({
        useful: res.useful,
        reason: res.reason,
        rewarded: res.rewarded,
      });
      if (res.useful) {
        setText("");
        localStorage.removeItem("feedback_draft");
      }
      stateQuery.refetch();
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert((e as Error).message);
    }
  };

  // Подарунок уже зароблений, але ще не використаний: форму теж не показуємо
  // (owner 2026-08-11: «закрив попап — і знову сторінка з опитуванням»).
  // Замість неї — екран із подарунком і кнопкою, що відкриває попап знову.
  if (rewardState === "active" && feedbackGift) {
    return (
      <>
        <BackNav />
        <PageHeader
          eyebrow="опитування"
          title="Дякуємо за відгук"
          titleLead="Дякуємо"
          titleAccent="за відгук."
          scribbleWidth={185}
          subtitle="Ми вже читаємо те, що ти написав. Подарунок за нього чекає — забирай."
        />

        <button
          type="button"
          onClick={() => setGiftShown(false)}
          className="w-full rounded-ios-card bg-primary text-white py-3.5 font-display text-[0.9375rem] font-bold active:bg-primary/90 transition"
        >
          {feedbackGift.button || "Забрати подарунок"}
        </button>

        {!giftShown && (
          <GiftOfferModal
            gift={feedbackGift}
            onClose={() => setGiftShown(true)}
            onGoToTariffs={() => setGiftShown(true)}
          />
        )}
      </>
    );
  }

  // Збір закрито (owner 2026-08-28: «люди все продовжують його писати і брати
  // підписку»). Посилання на сторінку лишились у людей у переписці з ботом,
  // тому екран має бути спокійним і зрозумілим, а не помилкою.
  if (stateQuery.data?.is_open === false) {
    return (
      <>
        <BackNav />
        <PageHeader
          eyebrow="опитування"
          title="Збір відгуків завершено"
          titleLead="Збір відгуків"
          titleAccent="завершено."
          scribbleWidth={210}
          subtitle="Дякуємо всім, хто написав — ми зібрали достатньо і вже працюємо над тим, про що нас просили."
        />
        <button
          type="button"
          onClick={() => navigate("/")}
          className="w-full rounded-ios-card bg-primary text-white py-3.5 font-display text-[0.9375rem] font-bold active:bg-primary/90 transition"
        >
          На головну
        </button>
      </>
    );
  }

  // Уже забрав подарунок — форму не показуємо, кажемо прямо.
  if (rewardState === "used") {
    return (
      <>
        <BackNav />
        <PageHeader
          eyebrow="опитування"
          title="Дякуємо за відгук"
          titleLead="Дякуємо"
          titleAccent="за відгук."
          scribbleWidth={185}
          subtitle="Ти вже скористався цією пропозицією. Якщо захочеш додати ще думок — просто напиши в підтримку, ми читаємо все."
        />
        <button
          type="button"
          onClick={() => navigate("/")}
          className="w-full rounded-ios-card bg-primary text-white py-3.5 font-display text-[0.9375rem] font-bold active:bg-primary/90 transition"
        >
          На головну
        </button>
      </>
    );
  }

  return (
    <>
      <BackNav />

      <PageHeader
        eyebrow="опитування"
        title="Клієнт знає краще"
        titleLead="Клієнт"
        titleAccent="знає краще."
        scribbleWidth={215}
        subtitle="Розкажи, чого тобі не вистачає в inFLOW і що зробило б його незамінним. Ми будуємо продукт із того, що пишуть клієнти."
      />

      <Section>
        <div className="px-2.5 py-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            // Owner 2026-08-11: без «наприклад» — просто запрошення писати.
            placeholder="Пиши сюди…"
            // caret-primary: рідний курсор на прозорому тлі був ледь видимий —
            // людина не бачила, де закінчується текст (owner 2026-08-12).
            className="w-full resize-none bg-transparent text-[0.875rem] leading-relaxed text-ink caret-primary selection:bg-primary/20 placeholder:text-ink-faint focus:outline-none"
          />
          {/* Owner 2026-08-11: лічильник спроб, обіцянка нагороди і рядок
              «відгук бачить лише команда» прибрані — лишаємо тільки лічильник
              символів, щоб сторінка не була напханою текстом. */}
          {/* Owner 2026-08-12: ліворуч — просто лічильник написаного,
              праворуч — словами, скільки потрібно. Дріб «0/500» прибрано. */}
          <div className="mt-1 flex items-center justify-between border-t border-ink/5 pt-2.5">
            <span
              className={
                "font-mono text-[0.6875rem] tabular-nums " +
                (enough ? "text-emerald-600" : "text-ink-faint")
              }
            >
              {len}
            </span>
            <span className="font-mono text-[0.6875rem] text-ink-faint">
              мінімум {min} символів
            </span>
          </div>
        </div>
      </Section>

      {/* Голосовий ввід (owner 2026-08-11): кнопку можна тиснути кілька разів
          — розшифроване ДОПИСУЄТЬСЯ до вже написаного, а не замінює його. */}
      <div className="mb-2.5">
        <VoiceBioButton
          mode="raw"
          size="lg"
          label="Наговорити голосом"
          onResult={(t) =>
            setText((prev) => (prev.trim() ? prev.trimEnd() + " " + t : t))
          }
        />
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSend}
        className="w-full rounded-ios-card bg-primary text-white py-3.5 font-display text-[0.9375rem] font-bold active:bg-primary/90 transition disabled:opacity-40"
      >
        {submitMut.isPending ? "Читаємо твій відгук…" : "Надіслати відгук"}
      </button>

      <div className="pb-4" />

      {result && (
        <FeedbackResultModal
          useful={result.useful}
          reason={result.reason}
          rewarded={result.rewarded}
          onClose={() => setResult(null)}
        />
      )}
    </>
  );
}
